import type { LucideIcon } from "lucide-react";
import {
  ArrowRight,
  HeartHandshake,
  MessageCircleHeart,
  Plus,
  Settings,
  Share2,
  Users,
} from "lucide-react";
import { useId, useState } from "react";
import { Link } from "wouter";
import type { ChildSummary } from "../../lib/api/useChildren";
import { ACCENTS, accentForIndex, type AccentName } from "../../lib/accents";
import { childShareHref } from "../../lib/childLinks";
import { IconChip } from "../ui/IconChip";
import { Button } from "../ui/Button";
import { Card } from "../ui/Card";
import { ChildAvatar } from "../ui/ChildAvatar";
import { DialogShell } from "../ui/DialogShell";
import { cn } from "../../lib/cn";
import { FOCUS_RING } from "../../lib/focusRing";
import { ROW } from "../../lib/surfaces";
import { useFeatureEnabled } from "../../features/_sim/flags"; // SIM-SEAM: community + stories actions

interface QuickAction {
  id: string;
  label: string;
  description: string;
  icon: LucideIcon;
  accent: AccentName;
  /** A link, or (when absent) a button that calls onSelect. */
  href?: string;
  onSelect?: () => void;
}

export interface QuickActionsProps {
  /** The family's child profiles (empty for a fresh account). */
  profiles: readonly ChildSummary[];
  /** Noun-aware "Add child" / "Add patient" label from the dashboard. */
  addLabel: string;
  /** Show the org-admin shortcut only when acting within an organization. */
  showOrgAdmin: boolean;
}

/** The settings pages, reached from the quiet line beside the heading. */
const SETTINGS_LINKS = [
  { href: "/settings/notifications", label: "Notifications" },
  { href: "/settings/security", label: "Security" },
  { href: "/settings/region", label: "Region" },
] as const;

/**
 * What a parent comes here to do ("very product-centric"): share a care link,
 * write in the community, share a family story, add another child. Sharing
 * and "Add child" need a profile, so a fresh account (which has "Add your
 * first child" twice already) sees only the two community actions; community
 * and stories actions follow their feature switches. The settings pages hang
 * off one quiet line beside the heading instead of taking tiles.
 *
 * Two columns on a phone (compact: icon over label) and a tablet, four across
 * from `xl` (three at `lg` when there are three); an odd last tile takes the
 * full row, so no row is left with an empty slot.
 */
export function QuickActions({ profiles, addLabel, showOrgAdmin }: QuickActionsProps) {
  const headingId = useId();
  const [pickerOpen, setPickerOpen] = useState(false);
  const communityOn = useFeatureEnabled("community");
  const storiesOn = useFeatureEnabled("stories");
  const only = profiles.length === 1 ? profiles[0] : undefined;

  const actions: QuickAction[] = [
    ...(profiles.length > 0
      ? [
          {
            id: "share",
            label: "Share a care link",
            description: only ? `${only.name}'s care profile` : "Choose whose profile to share",
            icon: Share2,
            accent: "violet",
            ...(only ? { href: childShareHref(only.id) } : { onSelect: () => setPickerOpen(true) }),
          } satisfies QuickAction,
        ]
      : []),
    ...(communityOn
      ? [
          {
            id: "community",
            label: "Write in Community",
            description: "Ask or share with other parents",
            icon: MessageCircleHeart,
            accent: "rose",
            href: "/community?compose=1",
          } satisfies QuickAction,
        ]
      : []),
    ...(storiesOn
      ? [
          {
            id: "story",
            label: "Share your family story",
            description: "Help another family feel less alone",
            icon: HeartHandshake,
            accent: "amber",
            href: `/stories/write?category=${encodeURIComponent("Family stories")}`,
          } satisfies QuickAction,
        ]
      : []),
    ...(profiles.length > 0
      ? [
          {
            id: "add",
            label: addLabel,
            description: "Start another care profile",
            icon: Plus,
            accent: "sky",
            href: "/child/new",
          } satisfies QuickAction,
        ]
      : []),
    ...(showOrgAdmin
      ? [
          {
            id: "org",
            label: "Org admin",
            description: "Manage your team",
            icon: Users,
            accent: "indigo",
            href: "/org/admin",
          } satisfies QuickAction,
        ]
      : []),
  ];

  return (
    // data-tour: the welcome tour's "Quick actions" stop.
    <section aria-labelledby={headingId} data-tour="quick-actions">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <h2
          id={headingId}
          className="font-display text-xl font-semibold leading-tight text-neutral-900 sm:text-[1.4rem]"
        >
          Quick actions
        </h2>
        <nav aria-label="Settings" className="flex flex-wrap items-center gap-x-0.5 gap-y-1 text-sm">
          <span className="mr-1 inline-flex items-center gap-1.5 text-neutral-600">
            <Settings className="h-4 w-4" aria-hidden />
            Settings
          </span>
          {SETTINGS_LINKS.map(({ href, label }) => (
            <Link
              key={href}
              href={href}
              className={cn(
                "rounded-full px-2.5 py-1 font-medium text-sky-700 transition hover:bg-white/80 hover:text-sky-900",
                FOCUS_RING,
              )}
            >
              {label}
            </Link>
          ))}
        </nav>
      </div>
      {actions.length > 0 && (
        <div
          className={cn(
            // Literal classes per count (Tailwind can't see interpolated names).
            // Four across only from xl: at lg a quarter row wraps the labels.
            "mt-4 grid grid-cols-2 gap-3 sm:gap-4 [&>*:last-child:nth-child(odd)]:col-span-2",
            actions.length === 4 && "xl:grid-cols-4",
            actions.length === 3 && "lg:grid-cols-3 lg:[&>*:last-child:nth-child(odd)]:col-span-1",
            actions.length >= 5 && "lg:grid-cols-3",
          )}
        >
          {actions.map((action) => (
            <ActionCard key={action.id} action={action} />
          ))}
        </div>
      )}
      {profiles.length > 1 && (
        <SharePicker profiles={profiles} open={pickerOpen} onOpenChange={setPickerOpen} />
      )}
    </section>
  );
}

function ActionCard({ action }: { action: QuickAction }) {
  const { href, onSelect, label, description, icon, accent } = action;
  const a = ACCENTS[accent];
  const inner = (
    <>
      <IconChip icon={icon} accent={accent} size="sm" />
      <span className="min-w-0 flex-1">
        <span className="block text-[15px] font-semibold leading-snug text-neutral-900 sm:text-base">
          {label}
        </span>
        <span className="hidden truncate text-xs text-neutral-600 sm:block">{description}</span>
      </span>
      <ArrowRight
        className={cn(
          // A hover hint on the wide two-column tiles; four across it would squeeze the label.
          "hidden h-4 w-4 shrink-0 opacity-0 transition group-hover:opacity-100 sm:block xl:hidden",
          a.text,
        )}
        aria-hidden
      />
    </>
  );
  // Compact on a phone (icon over label, no description), a row from `sm` up.
  const tile = cn(
    "group flex h-full w-full flex-col items-start gap-2 text-left sm:flex-row sm:items-center sm:gap-3",
    FOCUS_RING,
  );
  return (
    // Plain glass tile; the round accent pebble carries the colour.
    <Card asChild interactive className="relative overflow-hidden p-3.5 sm:p-4">
      {href ? (
        <Link href={href} className={tile}>
          {inner}
        </Link>
      ) : (
        <button type="button" onClick={onSelect} className={tile} aria-haspopup="dialog">
          {inner}
        </button>
      )}
    </Card>
  );
}

/** Several children: pick whose Sharing tab to open. */
function SharePicker({
  profiles,
  open,
  onOpenChange,
}: {
  profiles: readonly ChildSummary[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <DialogShell
      open={open}
      onOpenChange={onOpenChange}
      title="Share a care link"
      description="Whose care profile would you like to share? You'll choose what the caregiver sees next."
    >
      <ul className="space-y-2.5">
        {profiles.map((child, i) => (
          <li key={child.id}>
            <Link
              href={childShareHref(child.id)}
              onClick={() => onOpenChange(false)}
              className={cn(
                ROW,
                "group flex items-center gap-3 p-3 transition hover:bg-white",
                FOCUS_RING,
              )}
            >
              <span
                aria-hidden
                className={cn(
                  "inline-flex shrink-0 rounded-full bg-gradient-to-br p-0.5",
                  ACCENTS[accentForIndex(i)].gradient,
                )}
              >
                <ChildAvatar name={child.name} photoUrl={child.photoUrl} className="h-9 w-9 ring-2 ring-white" />
              </span>
              <span className="min-w-0 flex-1 font-semibold text-neutral-900">{child.name}</span>
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-violet-700">
                Share
                <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" aria-hidden />
              </span>
            </Link>
          </li>
        ))}
      </ul>
      <div className="mt-6 flex justify-end">
        <Button variant="secondary" size="sm" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
      </div>
    </DialogShell>
  );
}
